import { state } from "../state";
import { SpineEntry, SpineMeta } from "../types";
import {
  extractVersionFromBinary,
  mapSkeletonVersionToRuntime,
  patchAtlasTextIfNeeded,
  parseAtlasPageNames
} from "../utils/spine-utils";

export async function prepareMetaForEntry(entry: SpineEntry): Promise<SpineMeta> {
  const existing = state.spineMetaById[entry.id];
  if (existing && existing.runtimeVersion) {
    return existing;
  }

  if (existing && existing.sourceType === "local") {
    if (!existing.runtimeVersion) {
      existing.runtimeVersion = mapSkeletonVersionToRuntime(
        existing.version ?? null
      );
      state.spineMetaById[entry.id] = existing;
    }
    return existing;
  }

  const meta: SpineMeta = existing ?? {
    id: entry.id,
    sourceType: "manifest",
    skeletonPath: entry.skelUrl ?? entry.jsonUrl ?? undefined,
    atlasPath: entry.atlasUrl ?? undefined
  };

  let totalBytes = meta.totalBytes ?? 0;
  let version = meta.version ?? null;

  let jsonUrlToFetch = entry.jsonUrl;
  let skelUrlToFetch = entry.skelUrl;
  let atlasUrlToFetch = entry.atlasUrl;

  if (jsonUrlToFetch) {
    try {
      const isDataUrl = jsonUrlToFetch.startsWith('data:');
      const isBlobUrl = jsonUrlToFetch.startsWith('blob:');
      const url = (isDataUrl || isBlobUrl) ? jsonUrlToFetch : (/^[a-z]+:\/\//i.test(jsonUrlToFetch) ? jsonUrlToFetch : encodeURI(jsonUrlToFetch));
      const response = await fetch(url, { cache: "no-cache" });
      if (response.ok) {
        const text = await response.text();
        totalBytes += new Blob([text]).size;
        try {
          const json = JSON.parse(text) as any;
          const skeletonInfo = json.skeleton;
          if (skeletonInfo) {
            if (!version && typeof skeletonInfo.spine === "string") {
              version = skeletonInfo.spine;
            } else if (!version && typeof skeletonInfo.version === "string") {
              version = skeletonInfo.version;
            }
          }
          if (!meta.animationNames && json.animations) {
            if (typeof json.animations === "object") {
              meta.animationNames = Object.keys(json.animations);
            }
          }
          if (!meta.skinNames && json.skins) {
            if (typeof json.skins === "object") {
              meta.skinNames = Object.keys(json.skins);
            }
          }
        } catch (e) {
            console.error("Failed to parse JSON for entry:", entry.id, e);
        }
      } else {
        console.error("Failed to fetch JSON for entry:", entry.id, response.statusText);
      }
    } catch (e) {
        console.error("Error fetching JSON for entry:", entry.id, e);
    }
  } else if (skelUrlToFetch) {
    try {
      const isDataUrl = skelUrlToFetch.startsWith('data:');
      const isBlobUrl = skelUrlToFetch.startsWith('blob:');
      const url = (isDataUrl || isBlobUrl) ? skelUrlToFetch : (/^[a-z]+:\/\//i.test(skelUrlToFetch) ? skelUrlToFetch : encodeURI(skelUrlToFetch));
      const response = await fetch(url, { cache: "no-cache" });
      if (response.ok) {
        const buffer = await response.arrayBuffer();
        totalBytes += buffer.byteLength;
        if (!version) {
          version = extractVersionFromBinary(buffer);
        }
        
        const blob = new Blob([buffer]);
        meta.skeletonDataUri = URL.createObjectURL(blob);
      } else {
        console.error("Failed to fetch skel for entry:", entry.id, response.statusText);
      }
    } catch (e) {
        console.error("Error fetching skel for entry:", entry.id, e);
    }
  }

  if (atlasUrlToFetch) {
    try {
      const isDataUrl = atlasUrlToFetch.startsWith('data:');
      const isBlobUrl = atlasUrlToFetch.startsWith('blob:');
      const url = (isDataUrl || isBlobUrl) ? atlasUrlToFetch : (/^[a-z]+:\/\//i.test(atlasUrlToFetch) ? atlasUrlToFetch : encodeURI(atlasUrlToFetch));
      const response = await fetch(url, { cache: "no-cache" });
      if (response.ok) {
        const originalText = await response.text();
        let patchedText = patchAtlasTextIfNeeded(originalText);
        
        totalBytes += new Blob([patchedText]).size;
        const pages = parseAtlasPageNames(patchedText);
        meta.pngPageNames = pages;
        meta.atlasPageCount = pages.length;
        
        meta.atlasDataUri =
          "data:text/plain;charset=utf-8," + encodeURIComponent(patchedText);
      } else {
        console.error("Failed to fetch atlas for entry:", entry.id, response.statusText);
      }
    } catch (e) {
        console.error("Error fetching atlas for entry:", entry.id, e);
    }
  }

  meta.totalBytes = totalBytes > 0 ? totalBytes : undefined;
  meta.version = version ?? undefined;
  meta.runtimeVersion = mapSkeletonVersionToRuntime(version);
  state.spineMetaById[entry.id] = meta;
  return meta;
}
